/**
 * Улучшенные анимации для боя
 */

/**
 * Создает менеджер для улучшенных анимаций
 */
export function createEnhancedAnimationsManager() {
  /**
   * Улучшенная анимация урона с траекторией
   */
  function showEnhancedDamage(element, damage, isCrit = false, options = {}) {
    if (!element || !damage) return;
    
    const {
      offsetX = 0,
      offsetY = 0,
      color = isCrit ? '#ff0000' : '#ff6b6b',
      size = isCrit ? 24 : 18,
      duration = 1000
    } = options;
    
    const damageElement = document.createElement('div');
    damageElement.className = `battle-damage-enhanced ${isCrit ? 'battle-damage-enhanced--crit' : ''}`;
    damageElement.textContent = isCrit ? `💥 ${Math.round(damage)}` : `-${Math.round(damage)}`;
    
    // Позиционирование относительно элемента
    const rect = element.getBoundingClientRect();
    const startX = rect.left + rect.width / 2 + offsetX;
    const startY = rect.top + rect.height / 2 + offsetY;
    
    damageElement.style.cssText = `
      position: fixed;
      left: ${startX}px;
      top: ${startY}px;
      color: ${color};
      font-weight: 700;
      font-size: ${size}px;
      pointer-events: none;
      z-index: 10000;
      text-shadow: 0 0 10px ${color}, 0 0 20px ${color};
      transform: translate(-50%, -50%);
      will-change: transform, opacity;
    `;
    
    document.body.appendChild(damageElement);
    
    // Проверяем поддержку Web Animations API
    const supportsWebAnimations = typeof damageElement.animate === 'function';
    
    if (supportsWebAnimations) {
      // Анимация через Web Animations API (современные браузеры)
      const animation = damageElement.animate([
        {
          transform: 'translate(-50%, -50%) scale(0.8)',
          opacity: 0
        },
        {
          transform: 'translate(-50%, -70px) scale(1.2)',
          opacity: 1,
          offset: 0.3
        },
        {
          transform: 'translate(-50%, -100px) scale(1)',
          opacity: 0.8,
          offset: 0.7
        },
        {
          transform: 'translate(-50%, -130px) scale(0.9)',
          opacity: 0
        }
      ], {
        duration: duration,
        easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      });
      
      animation.onfinish = () => {
        if (damageElement.parentNode) {
          damageElement.parentNode.removeChild(damageElement);
        }
      };
      
      return animation;
    } else {
      // Fallback через CSS анимацию (старые браузеры, включая некоторые Android)
      damageElement.style.cssText += `
        animation: damageFloatFallback ${duration}ms ease-out forwards;
      `;
      
      // Добавляем CSS анимацию если её нет
      if (!document.getElementById('damage-animation-fallback-style')) {
        const style = document.createElement('style');
        style.id = 'damage-animation-fallback-style';
        style.textContent = `
          @keyframes damageFloatFallback {
            0% {
              transform: translate(-50%, -50%) scale(0.8);
              opacity: 0;
            }
            30% {
              transform: translate(-50%, -70px) scale(1.2);
              opacity: 1;
            }
            70% {
              transform: translate(-50%, -100px) scale(1);
              opacity: 0.8;
            }
            100% {
              transform: translate(-50%, -130px) scale(0.9);
              opacity: 0;
            }
          }
        `;
        document.head.appendChild(style);
      }
      
      setTimeout(() => {
        if (damageElement.parentNode) {
          damageElement.parentNode.removeChild(damageElement);
        }
      }, duration);
      
      return null;
    }
  }
  
  /**
   * Анимация shake для элемента при получении урона
   */
  function shakeElement(element, intensity = 10, duration = 300) {
    if (!element) return;
    
    const supportsWebAnimations = typeof element.animate === 'function';
    const originalTransform = element.style.transform || '';
    
    if (supportsWebAnimations) {
      const animation = element.animate([
        { transform: 'translateX(0)' },
        { transform: `translateX(-${intensity}px)` },
        { transform: `translateX(${intensity}px)` },
        { transform: `translateX(-${intensity/2}px)` },
        { transform: `translateX(${intensity/2}px)` },
        { transform: 'translateX(0)' }
      ], {
        duration: duration,
        easing: 'ease-in-out'
      });
      
      animation.onfinish = () => {
        element.style.transform = originalTransform;
      };
      
      return animation;
    } else {
      // Fallback через CSS класс
      const shakeClass = 'battle-shake-fallback';
      element.classList.add(shakeClass);
      
      // Добавляем CSS если его нет
      if (!document.getElementById('shake-animation-fallback-style')) {
        const style = document.createElement('style');
        style.id = 'shake-animation-fallback-style';
        style.textContent = `
          .battle-shake-fallback {
            animation: shakeFallback ${duration}ms ease-in-out;
          }
          @keyframes shakeFallback {
            0%, 100% { transform: translateX(0); }
            25% { transform: translateX(-${intensity}px); }
            50% { transform: translateX(${intensity}px); }
            75% { transform: translateX(-${intensity/2}px); }
          }
        `;
        document.head.appendChild(style);
      }
      
      setTimeout(() => {
        element.classList.remove(shakeClass);
        element.style.transform = originalTransform;
      }, duration);
      
      return null;
    }
  }
  
  /**
   * Плавная анимация HP бара
   */
  function animateHPBar(barElement, fromPercent, toPercent, duration = 500) {
    if (!barElement) return;
    
    const startTime = performance.now();
    const difference = toPercent - fromPercent;
    
    function animate(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing функция (ease-out)
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = fromPercent + (difference * eased);
      
      barElement.style.width = `${Math.max(0, Math.min(100, current))}%`;
      
      // Цветовая индикация
      if (current > 60) {
        barElement.style.backgroundColor = '#22c55e'; // зеленый
      } else if (current > 30) {
        barElement.style.backgroundColor = '#eab308'; // желтый
      } else {
        barElement.style.backgroundColor = '#ef4444'; // красный
      }
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    }
    
    requestAnimationFrame(animate);
  }
  
  /**
   * Анимация восстановления HP (зеленый цвет)
   */
  function animateHeal(element, healAmount) {
    if (!element || !healAmount) return;
    
    const healElement = document.createElement('div');
    healElement.className = 'battle-heal';
    healElement.textContent = `+${Math.round(healAmount)}`;
    healElement.style.cssText = `
      position: absolute;
      color: #22c55e;
      font-weight: 700;
      font-size: 16px;
      pointer-events: none;
      z-index: 1000;
      text-shadow: 0 0 10px #22c55e;
    `;
    
    element.style.position = 'relative';
    element.appendChild(healElement);
    
    const supportsWebAnimations = typeof healElement.animate === 'function';
    
    if (supportsWebAnimations) {
      const animation = healElement.animate([
        {
          transform: 'translateY(0) scale(0.8)',
          opacity: 0
        },
        {
          transform: 'translateY(-30px) scale(1.1)',
          opacity: 1,
          offset: 0.5
        },
        {
          transform: 'translateY(-60px) scale(1)',
          opacity: 0
        }
      ], {
        duration: 800,
        easing: 'ease-out'
      });
      
      animation.onfinish = () => {
        if (healElement.parentNode) {
          healElement.parentNode.removeChild(healElement);
        }
      };
    } else {
      // Fallback через CSS
      healElement.style.cssText += `
        animation: healFloatFallback 800ms ease-out forwards;
      `;
      
      if (!document.getElementById('heal-animation-fallback-style')) {
        const style = document.createElement('style');
        style.id = 'heal-animation-fallback-style';
        style.textContent = `
          @keyframes healFloatFallback {
            0% {
              transform: translateY(0) scale(0.8);
              opacity: 0;
            }
            50% {
              transform: translateY(-30px) scale(1.1);
              opacity: 1;
            }
            100% {
              transform: translateY(-60px) scale(1);
              opacity: 0;
            }
          }
        `;
        document.head.appendChild(style);
      }
      
      setTimeout(() => {
        if (healElement.parentNode) {
          healElement.parentNode.removeChild(healElement);
        }
      }, 800);
    }
  }
  
  /**
   * Эффект критического удара
   */
  function showCritEffect(element) {
    if (!element) return;
    
    const critEffect = document.createElement('div');
    critEffect.className = 'battle-crit-effect';
    critEffect.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      width: 200px;
      height: 200px;
      margin: -100px 0 0 -100px;
      pointer-events: none;
      z-index: 9999;
      background: radial-gradient(circle, rgba(255,0,0,0.3) 0%, transparent 70%);
      border-radius: 50%;
    `;
    
    element.style.position = 'relative';
    element.appendChild(critEffect);
    
    const animation = critEffect.animate([
      {
        transform: 'scale(0)',
        opacity: 1
      },
      {
        transform: 'scale(1.5)',
        opacity: 0
      }
    ], {
      duration: 400,
      easing: 'ease-out'
    });
    
    animation.onfinish = () => {
      if (critEffect.parentNode) {
        critEffect.parentNode.removeChild(critEffect);
      }
    };
  }
  
  /**
   * Анимация применения статуса
   */
  function animateStatusApply(element, statusIcon, statusName) {
    if (!element) return;
    
    const statusElement = document.createElement('div');
    statusElement.className = 'battle-status-apply';
    statusElement.innerHTML = `${statusIcon} ${statusName}`;
    statusElement.style.cssText = `
      position: absolute;
      top: -20px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0, 0, 0, 0.8);
      color: #fff;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
      pointer-events: none;
      z-index: 1000;
      white-space: nowrap;
    `;
    
    element.style.position = 'relative';
    element.appendChild(statusElement);
    
    const animation = statusElement.animate([
      {
        transform: 'translateX(-50%) translateY(0)',
        opacity: 0
      },
      {
        transform: 'translateX(-50%) translateY(-10px)',
        opacity: 1,
        offset: 0.3
      },
      {
        transform: 'translateX(-50%) translateY(-30px)',
        opacity: 0
      }
    ], {
      duration: 1000,
      easing: 'ease-out'
    });
    
    animation.onfinish = () => {
      if (statusElement.parentNode) {
        statusElement.parentNode.removeChild(statusElement);
      }
    };
  }
  
  /**
   * Эффект победы
   */
  function showVictoryEffect(container) {
    if (!container) return;
    
    const victoryElement = document.createElement('div');
    victoryElement.className = 'battle-victory-effect';
    victoryElement.innerHTML = '🎉 VICTORY! 🎉';
    victoryElement.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 48px;
      font-weight: 700;
      color: #ffd700;
      text-shadow: 0 0 20px #ffd700, 0 0 40px #ffd700;
      z-index: 10000;
      pointer-events: none;
      animation: victoryPulse 2s ease-in-out;
    `;
    
    document.body.appendChild(victoryElement);
    
    // Добавляем CSS анимацию если её нет
    if (!document.getElementById('victory-animation-style')) {
      const style = document.createElement('style');
      style.id = 'victory-animation-style';
      style.textContent = `
        @keyframes victoryPulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
          50% { transform: translate(-50%, -50%) scale(1.2); opacity: 0.9; }
        }
      `;
      document.head.appendChild(style);
    }
    
    setTimeout(() => {
      if (victoryElement.parentNode) {
        victoryElement.parentNode.removeChild(victoryElement);
      }
    }, 2000);
  }
  
  return {
    showEnhancedDamage,
    shakeElement,
    animateHPBar,
    animateHeal,
    showCritEffect,
    animateStatusApply,
    showVictoryEffect
  };
}

